import React from "react";
import Header from "@/components/Header";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import Footer from "@/components/Footer";
import Container from "@/components/blocks/Container";
import Link from "next/link";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import { docsColors, docsComponents, alertVariants } from "@/lib/docs-theme";
import { DocNavFooter } from "@/components/docs";

export default function StatusPagesOverviewPage() {
  return (
    <>
      <Head>
        {generateNextSeo({
          title: "Status Pages - Uptime Monitor Documentation",
          description:
            "Create beautiful public status pages to keep customers informed. Real-time updates, custom branding, and subscriber notifications.",
          canonical: "https://uptime-monitor.io/docs/status-pages",
        })}
      </Head>
      <div className={docsComponents.pageContainer}>
        <Header />
        <main className="flex-1">
          <Container>
            <div className="mx-auto max-w-4xl px-4 py-12">
              {/* Breadcrumb */}
              <nav className="mb-8">
                <ol className="flex items-center space-x-2 text-sm">
                  <li>
                    <Link href="/docs" className={docsColors.link.muted}>
                      Documentation
                    </Link>
                  </li>
                  <li className={docsColors.text.muted}>/</li>
                  <li className={docsColors.text.heading}>Status Pages</li>
                </ol>
              </nav>

              {/* Page Title */}
              <h1 className={`text-4xl font-bold ${docsColors.text.heading} mb-4`}>Status Pages</h1>
              <p className={`text-xl ${docsColors.text.body} mb-8`}>
                Create public status pages to display real-time uptime and performance data for your
                services.
              </p>

              {/* What are Status Pages */}
              <section className="mb-12">
                <div className={`${alertVariants.info} rounded-lg p-6 border`}>
                  <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
                    What are Status Pages?
                  </h2>
                  <p className={`${docsColors.text.body} mb-4`}>
                    Status pages are public websites that display the real-time operational status
                    of your services. They show which services are up or down and historical uptime
                    percentages.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2`}>✓</span>
                      Real-time status of monitored services
                    </li>
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2`}>✓</span>
                      Historical uptime percentages (7, 30, or 90 days)
                    </li>
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2`}>✓</span>
                      Response time graphs for each monitor
                    </li>
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2`}>✓</span>
                      Incident history with timestamps
                    </li>
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2`}>✓</span>
                      Optional password protection (paid plans)
                    </li>
                  </ul>
                </div>
              </section>

              {/* Key Benefits */}
              <section className="mb-12">
                <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
                  Key Benefits
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className={`${docsComponents.card} p-6`}>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                      Transparency
                    </h3>
                    <p className={docsColors.text.body}>
                      Let customers check service status without contacting support.
                    </p>
                  </div>

                  <div className={`${docsComponents.card} p-6`}>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                      Reduce Support Load
                    </h3>
                    <p className={docsColors.text.body}>
                      Customers can self-serve status information during incidents.
                    </p>
                  </div>

                  <div className={`${docsComponents.card} p-6`}>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                      Track Performance
                    </h3>
                    <p className={docsColors.text.body}>
                      Display uptime percentages and response times over time.
                    </p>
                  </div>

                  <div className={`${docsComponents.card} p-6`}>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                      Document Reliability
                    </h3>
                    <p className={docsColors.text.body}>
                      Historical data helps with SLA compliance and reporting.
                    </p>
                  </div>
                </div>
              </section>

              {/* How It Works */}
              <section className="mb-12">
                <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
                  How Status Pages Work
                </h2>
                <div className={`${docsComponents.card} p-6`}>
                  <ol className="space-y-4">
                    <li className="flex items-start">
                      <span
                        className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                      >
                        1
                      </span>
                      <div>
                        <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                          Create Your Status Page
                        </h3>
                        <p className={`${docsColors.text.body} text-sm`}>
                          Name your page and select which monitors to include
                        </p>
                      </div>
                    </li>

                    <li className="flex items-start">
                      <span
                        className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                      >
                        2
                      </span>
                      <div>
                        <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                          Select Monitors
                        </h3>
                        <p className={`${docsColors.text.body} text-sm`}>
                          Choose which monitors to display on your status page
                        </p>
                      </div>
                    </li>

                    <li className="flex items-start">
                      <span
                        className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                      >
                        3
                      </span>
                      <div>
                        <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                          Automatic Updates
                        </h3>
                        <p className={`${docsColors.text.body} text-sm`}>
                          Status updates automatically when monitors detect changes
                        </p>
                      </div>
                    </li>

                    <li className="flex items-start">
                      <span
                        className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                      >
                        4
                      </span>
                      <div>
                        <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                          Share the Link
                        </h3>
                        <p className={`${docsColors.text.body} text-sm`}>
                          Share your status page URL with customers
                        </p>
                      </div>
                    </li>
                  </ol>
                </div>
              </section>

              {/* What's Included */}
              <section className="mb-12">
                <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
                  What&apos;s Included in Status Pages
                </h2>
                <div className="space-y-4">
                  <div className={`${docsComponents.card} p-4`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`font-semibold ${docsColors.text.heading}`}>
                          Overall Status
                        </h3>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          Shows if all monitors are operational or if any are down
                        </p>
                      </div>
                      <div
                        className={`bg-green-500 text-white px-3 py-1 rounded text-sm font-medium`}
                      >
                        All Systems Operational
                      </div>
                    </div>
                  </div>

                  <div className={`${docsComponents.card} p-4`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`font-semibold ${docsColors.text.heading}`}>Monitor List</h3>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          Each monitor with its current status and uptime percentage
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      </div>
                    </div>
                  </div>

                  <div className={`${docsComponents.card} p-4`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`font-semibold ${docsColors.text.heading}`}>
                          Uptime Percentage
                        </h3>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          Historical uptime for selected period (7/30/90 days)
                        </p>
                      </div>
                      <div className={docsColors.text.muted}>99.95%</div>
                    </div>
                  </div>

                  <div className={`${docsComponents.card} p-4`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`font-semibold ${docsColors.text.heading}`}>
                          Response Time Graph
                        </h3>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          Visual graph showing response times over the selected period
                        </p>
                      </div>
                      <div className={docsColors.text.muted}>Per monitor</div>
                    </div>
                  </div>

                  <div className={`${docsComponents.card} p-4`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`font-semibold ${docsColors.text.heading}`}>
                          Incident History
                        </h3>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          List of past incidents with timestamps and duration
                        </p>
                      </div>
                      <div className={docsColors.text.muted}>Optional reasons</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Use Cases */}
              <section className="mb-12">
                <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
                  Common Use Cases
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className={`${docsComponents.card} p-6`}>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                      SaaS Applications
                    </h3>
                    <ul className={`space-y-1 text-sm ${docsColors.text.body}`}>
                      <li>• Show API endpoint status</li>
                      <li>• Display app availability</li>
                      <li>• Track database uptime</li>
                      <li>• Monitor key services</li>
                    </ul>
                  </div>

                  <div className={`${docsComponents.card} p-6`}>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>E-commerce</h3>
                    <ul className={`space-y-1 text-sm ${docsColors.text.body}`}>
                      <li>• Checkout system status</li>
                      <li>• Payment gateway health</li>
                      <li>• API availability</li>
                      <li>• Store uptime</li>
                    </ul>
                  </div>

                  <div className={`${docsComponents.card} p-6`}>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                      Corporate IT
                    </h3>
                    <ul className={`space-y-1 text-sm ${docsColors.text.body}`}>
                      <li>• Internal service status</li>
                      <li>• VPN availability</li>
                      <li>• Email system health</li>
                      <li>• Application uptime</li>
                    </ul>
                  </div>

                  <div className={`${docsComponents.card} p-6`}>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                      Service Providers
                    </h3>
                    <ul className={`space-y-1 text-sm ${docsColors.text.body}`}>
                      <li>• API service status</li>
                      <li>• Platform availability</li>
                      <li>• Infrastructure health</li>
                      <li>• Service uptime</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Best Practices */}
              <section className="mb-12">
                <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
                  Best Practices
                </h2>
                <div className={`${docsComponents.card} p-6`}>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2 mt-0.5`}>✓</span>
                      <div>
                        <strong className={docsColors.text.heading}>
                          Choose relevant monitors
                        </strong>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          Only include customer-facing services on public pages
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2 mt-0.5`}>✓</span>
                      <div>
                        <strong className={docsColors.text.heading}>Use descriptive names</strong>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          Make monitor names clear for non-technical users
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2 mt-0.5`}>✓</span>
                      <div>
                        <strong className={docsColors.text.heading}>Consider time periods</strong>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          Choose 7, 30, or 90 days based on your needs
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2 mt-0.5`}>✓</span>
                      <div>
                        <strong className={docsColors.text.heading}>
                          Decide on incident reasons
                        </strong>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          Show reasons if transparency is important to your users
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className={`${docsColors.icon.success} mr-2 mt-0.5`}>✓</span>
                      <div>
                        <strong className={docsColors.text.heading}>Share the URL</strong>
                        <p className={`${docsColors.text.body} text-sm mt-1`}>
                          Link to your status page from your website and support docs
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Creating a Status Page */}
              <section className="mb-12">
                <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
                  Creating a Status Page
                </h2>

                <div className={`${docsComponents.card} p-6`}>
                  <ol className="space-y-4">
                    <li>
                      <div className="flex items-start">
                        <span
                          className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                        >
                          1
                        </span>
                        <div>
                          <p className={`${docsColors.text.body} text-sm`}>
                            Navigate to{" "}
                            <strong className={docsColors.text.heading}>Status Pages</strong> in
                            your dashboard
                          </p>
                        </div>
                      </div>
                    </li>

                    <li>
                      <div className="flex items-start">
                        <span
                          className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                        >
                          2
                        </span>
                        <div>
                          <p className={`${docsColors.text.body} text-sm`}>
                            Click{" "}
                            <strong className={docsColors.text.heading}>Create Status Page</strong>
                          </p>
                        </div>
                      </div>
                    </li>

                    <li>
                      <div className="flex items-start">
                        <span
                          className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                        >
                          3
                        </span>
                        <div>
                          <p className={`${docsColors.text.body} text-sm`}>
                            Enter a name and optional logo for your page
                          </p>
                        </div>
                      </div>
                    </li>

                    <li>
                      <div className="flex items-start">
                        <span
                          className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                        >
                          4
                        </span>
                        <div>
                          <p className={`${docsColors.text.body} text-sm`}>
                            Select which monitors to display
                          </p>
                        </div>
                      </div>
                    </li>

                    <li>
                      <div className="flex items-start">
                        <span
                          className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                        >
                          5
                        </span>
                        <div>
                          <p className={`${docsColors.text.body} text-sm`}>
                            Save your page and it will be live
                          </p>
                        </div>
                      </div>
                    </li>
                  </ol>
                </div>
              </section>

              {/* Custom Domain Setup */}
              <section className="mb-12">
                <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
                  Custom Domain Setup (Paid Plans)
                </h2>
                <div className={`${docsComponents.card} p-6`}>
                  <p className={`${docsColors.text.body} mb-4`}>
                    To use your own domain, you must be on a paid plan. You can set this up in your
                    status page settings.
                  </p>

                  <ol className="space-y-4">
                    <li>
                      <div className="flex items-start">
                        <span
                          className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                        >
                          1
                        </span>
                        <div>
                          <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                            Enter Custom Domain
                          </h3>
                          <p className={`${docsColors.text.body} text-sm`}>
                            In your status page settings, enter your desired domain (e.g.,
                            status.yourcompany.com)
                          </p>
                        </div>
                      </div>
                    </li>

                    <li>
                      <div className="flex items-start">
                        <span
                          className={`bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5`}
                        >
                          2
                        </span>
                        <div>
                          <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                            Configure DNS
                          </h3>
                          <p className={`${docsColors.text.body} text-sm`}>
                            Add a CNAME record in your DNS provider pointing your custom domain to
                            our servers.
                          </p>
                        </div>
                      </div>
                    </li>
                  </ol>

                  <div className={`mt-6 p-4 ${alertVariants.warning} rounded border`}>
                    <p className={`text-sm ${docsColors.text.body}`}>
                      <strong className={docsColors.text.heading}>Custom Domains:</strong> Custom
                      domain support is available on all plans. Configure your DNS to point to our
                      servers.
                    </p>
                  </div>
                </div>
              </section>

              {/* Additional Settings */}
              <section className="mb-12">
                <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
                  Additional Settings
                </h2>
                <div className={`${docsComponents.card} p-6`}>
                  <div>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                      Show Incident Reasons
                    </h3>
                    <p className={docsColors.text.body}>
                      Choose whether to display reasons for incidents on your status page.
                    </p>
                  </div>

                  <div>
                    <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                      Basic Authentication (Paid Plans)
                    </h3>
                    <p className={docsColors.text.body}>
                      Protect your status page with a username and password.
                    </p>
                  </div>
                </div>
              </section>

              <DocNavFooter
                prev={{ href: "/docs/notifications/zendesk", text: "Zendesk" }}
                next={{ href: "/docs/best-practices", text: "Best Practices" }}
              />
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    </>
  );
}
